import { useState, useCallback, useRef, useEffect } from 'react';
import { isSensitiveLog } from '../lib/consoleFilter';
import { Cpu, Plug, Power, RefreshCw, DownloadCloud, Save, CircuitBoard, FolderOpen, HardDriveDownload, Copy, Check } from 'lucide-react';
import { getSharedManager, runRenesasRead, runRenesasWrite } from '../lib/serial';
import { fetchBridgePorts } from '../lib/bridge';
import { saveBinaryWithPicker } from '../lib/saveAs';
import { SaveAsDialog } from './SaveAsDialog';
import { brandLog } from '../lib/brand';

const BRIDGE_URL = 'ws://localhost:8765';
const COMPACT = typeof window !== 'undefined' && (((!!window.matchMedia && window.matchMedia('(pointer: coarse)').matches)) || (typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0));

// Renesas serial boot-mode chips (Renesas Flash Programmer / FDT .fcf layouts).
// Regions are {start,size} for the page-read range (FF mid high).
const CHIPS = {
  R5F21548G: { label: 'R5F21548G · R8C 96KB', arch: 'R8C', regions: {
    user: { label: 'User Flash (0x8000 → 0x1FFFF, 96KB)', start: 0x8000, size: 0x18000 },
    user64: { label: 'User Flash 64KB (0x10000 → 0x1FFFF)', start: 0x10000, size: 0x10000 },
    data: { label: 'Data Flash (0x7000 → 0x7FFF, 4KB)', start: 0x7000, size: 0x1000 },
    full: { label: 'Full (0x7000 → 0x1FFFF)', start: 0x7000, size: 0x19000 },
    eb3: { label: 'EB3 · User (0x8000 → 0x9FFF, 8K)', start: 0x8000, size: 0x2000 },
    eb2: { label: 'EB2 · User (0xA000 → 0xBFFF, 8K)', start: 0xA000, size: 0x2000 },
    eb1: { label: 'EB1 · User (0xC000 → 0xDFFF, 8K)', start: 0xC000, size: 0x2000 },
    eb0: { label: 'EB0 · User (0xE000 → 0xFFFF, 8K)', start: 0xE000, size: 0x2000 },
    eb4: { label: 'EB4 · User (0x10000 → 0x17FFF, 32K)', start: 0x10000, size: 0x8000 },
    eb5: { label: 'EB5 · User (0x18000 → 0x1FFFF, 32K)', start: 0x18000, size: 0x8000 },
    eba: { label: 'EBA · Data (0x7000 → 0x73FF, 1K)', start: 0x7000, size: 0x400 },
    ebb: { label: 'EBB · Data (0x7400 → 0x77FF, 1K)', start: 0x7400, size: 0x400 },
    ebc: { label: 'EBC · Data (0x7800 → 0x7BFF, 1K)', start: 0x7800, size: 0x400 },
    ebd: { label: 'EBD · Data (0x7C00 → 0x7FFF, 1K)', start: 0x7C00, size: 0x400 },
  } },
  R5F21347H: { label: 'R5F21347H · R8C 48KB', arch: 'R8C', regions: {
    user: { label: 'User Flash (0x4000 → 0xFFFF, 48KB)', start: 0x4000, size: 0xC000, blocks: [
      { name: 'EB4', start: 0x4000, size: 0x4000 },
      { name: 'EB3', start: 0x8000, size: 0x4000 },
      { name: 'EB2', start: 0xC000, size: 0x2000 },
      { name: 'EB1', start: 0xE000, size: 0x1000 },
      { name: 'EB0', start: 0xF000, size: 0x1000 },
    ] },
    eb4: { label: 'EB4 (0x4000 → 0x7FFF, 16K)', start: 0x4000, size: 0x4000 },
    eb3: { label: 'EB3 (0x8000 → 0xBFFF, 16K)', start: 0x8000, size: 0x4000 },
    eb2: { label: 'EB2 (0xC000 → 0xDFFF, 8K)', start: 0xC000, size: 0x2000 },
    eb1: { label: 'EB1 (0xE000 → 0xEFFF, 4K)', start: 0xE000, size: 0x1000 },
    eb0: { label: 'EB0 (0xF000 → 0xFFFF, 4K)', start: 0xF000, size: 0x1000 },
  } },
  R5F35633: { label: 'R5F35633 · M16C 112KB', arch: 'M16C', regions: {
    user1: { label: 'User Flash 1 (0xE8000, 96KB)', start: 0xE8000, size: 0x18000 },
    user2: { label: 'User Flash 2 (0x10000, 16KB)', start: 0x10000, size: 0x4000 },
    data: { label: 'Data Flash (0xE000, 8KB)', start: 0xE000, size: 0x2000 },
  } },
};

const BAUDS = ['9600', '19200', '38400', '115200', '250000', '500000'];
const LEVELS = { INFO: 'text-slate-400', SUCCESS: 'text-emerald-400', ERROR: 'text-red-400', WARN: 'text-yellow-400' };
// R8C boot mode requires a 7-byte ID code check. A blank/unlocked chip reads FF x7
// (confirmed by Renesas FDT: "ID Code = 0xFF 0xFF 0xFF 0xFF 0xFF 0xFF 0xFF").
const defaultIdArr = (c) => (CHIPS[c]?.arch === 'R8C' ? Array(7).fill('FF') : Array(7).fill(''));

export const RenesasFlashTool = ({ compact, onBusyChange }) => {
  const CMP = compact !== undefined ? compact : COMPACT;
  const [chip, setChip] = useState('R5F21548G');
  const [regionKey, setRegionKey] = useState('user');
  const [baud, setBaud] = useState('9600');
  const [idArr, setIdArr] = useState(defaultIdArr('R5F21548G'));
  const [port, setPort] = useState('');
  const [ports, setPorts] = useState([]);
  const [portsLoading, setPortsLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { onBusyChange?.(busy || connected); }, [busy, connected, onBusyChange]);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(null);
  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState(null);
  const [saveAsReq, setSaveAsReq] = useState(null); // {bytes, name, meta} pending Save As…
  const [srcBytes, setSrcBytes] = useState(null);
  const [srcName, setSrcName] = useState('');
  const [erase, setErase] = useState(true);
  const [verify, setVerify] = useState(true);
  const [absoluteImage, setAbsoluteImage] = useState(true);
  const [skipFF, setSkipFF] = useState(true);
  const [op, setOp] = useState(null); // 'read' | 'write' | null — which action is running
  const fileRef = useRef(null);
  const consoleRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (autoScroll && consoleRef.current) consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
  }, [logs, autoScroll]);

  const onConsoleScroll = useCallback((e) => {
    const el = e.currentTarget;
    setAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 24);
  }, []);

  const copyLogs = useCallback(async () => {
    const text = logs.map((l, i) => `${String(i + 1).padStart(2, '0')} ${l.msg}`).join('\n');
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
      else { const ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  }, [logs]);

  const regions = CHIPS[chip].regions;
  const region = regions[regionKey] || Object.values(regions)[0];
  const manager = getSharedManager();

  const pushLog = useCallback((level, msg) => { if (isSensitiveLog(msg)) return; setLogs((L) => [...L.slice(-200), { level, msg: brandLog(msg) }]); }, []);

  const loadPorts = useCallback(async () => {
    setPortsLoading(true);
    const r = await fetchBridgePorts(BRIDGE_URL);
    setPortsLoading(false);
    if (r.busy) { pushLog('WARN', 'Flash Tools session active — port list unchanged.'); return; }
    if (!r.online) { setPorts([]); pushLog('ERROR', 'TRMD Engine not detected — run TRMDEngine.exe.'); return; }
    setPorts(r.ports);
    pushLog(r.ports.length ? 'INFO' : 'WARN', r.ports.length ? `COM ports: ${r.ports.join(', ')}` : 'No COM port found — connect the FTDI TTL adapter and install the VCP driver.');
  }, [pushLog]);

  const onChipChange = (c) => { setChip(c); setRegionKey(Object.keys(CHIPS[c].regions)[0]); setIdArr(defaultIdArr(c)); };
  const setIdByte = (i, v, el) => {
    const clean = v.replace(/[^0-9a-fA-F]/g, '').toUpperCase().slice(0, 2);
    setIdArr((a) => { const n = [...a]; n[i] = clean; return n; });
    if (clean.length === 2 && el && el.nextElementSibling) el.nextElementSibling.focus();
  };

  const doDisconnect = useCallback(async () => {
    try { await manager.disconnect(); } catch (_) {}
    setConnected(false);
    pushLog('INFO', 'Port closed.');
  }, [manager, pushLog]);

  const doRead = useCallback(async () => {
    setBusy(true); setOp('read'); setProgress(0); setResult(null); setPhase(null);
    const idClean = idArr.map((t) => (t ? t.padStart(2, '0') : '')).join('');
    try {
      pushLog('INFO', `Opening ${port || 'AUTO'} @ ${baud}…`);
      await manager.connectBridge({ url: BRIDGE_URL, baud: Number(baud), device: port || undefined });
      setConnected(true);
      pushLog('SUCCESS', `Port open.`);
    } catch (e) {
      pushLog('ERROR', `Port open failed: ${e.message}`);
      setBusy(false); setOp(null); setProgress(0);
      return;
    }
    const res = await runRenesasRead(manager, {
      baud: Number(baud), start: region.start, size: region.size, idhex: idClean, chip, blocks: region.blocks || [],
    }, {
      onLog: (lvl, msg) => pushLog(lvl, msg),
      onProgress: (_ph, p) => setProgress(p),
      onPhase: () => {},
    });
    setBusy(false); setOp(null); setProgress(0);
    if ((res.result === 'success' || res.result === 'partial') && res.bytes && res.bytes.length) {
      setResult(res.bytes);
    }
  }, [manager, baud, port, region, chip, idArr, pushLog]);

  const onFile = useCallback(async (f) => {
    if (!f) return;
    const b = new Uint8Array(await f.arrayBuffer());
    setSrcBytes(b); setSrcName(f.name);
    pushLog('INFO', `File loaded: ${f.name} · ${b.length} bytes`);
    // Auto-map the image to the matching region by size (like FDT maps the file to
    // User/Data Flash). Prevents writing a 64K User image at the wrong start offset.
    const rgs = CHIPS[chip].regions;
    const hit = Object.entries(rgs).find(([, r]) => r.size === b.length || (r.start + r.size) === b.length);
    if (hit) {
      setRegionKey(hit[0]);
      pushLog('SUCCESS', `Region auto-selected → ${hit[1].label} (start 0x${hit[1].start.toString(16).toUpperCase()})`);
    } else {
      pushLog('WARN', `File size (${b.length} B) does not match any region — select the region manually.`);
    }
  }, [pushLog, chip]);

  const doWrite = useCallback(async () => {
    if (!srcBytes || !srcBytes.length) { pushLog('ERROR', 'Load a .bin file before Write.'); return; }
    setBusy(true); setOp('write'); setProgress(0); setPhase(null);
    const idClean = idArr.map((t) => (t ? t.padStart(2, '0') : '')).join('');
    let writeBytes = srcBytes;
    if (absoluteImage && region.start > 0 && srcBytes.length >= region.start + region.size) {
      writeBytes = srcBytes.subarray(region.start, region.start + region.size);
      pushLog('INFO', `Absolute Image: using region 0x${region.start.toString(16).toUpperCase()}..0x${(region.start + region.size - 1).toString(16).toUpperCase()} from file (${srcBytes.length} B) → ${writeBytes.length} B to write.`);
    }
    try {
      pushLog('INFO', `Opening ${port || 'AUTO'} @ ${baud}…`);
      await manager.connectBridge({ url: BRIDGE_URL, baud: Number(baud), device: port || undefined });
      setConnected(true);
      pushLog('SUCCESS', `Port open.`);
    } catch (e) {
      pushLog('ERROR', `Port open failed: ${e.message}`); setBusy(false); setOp(null); setProgress(0); return;
    }
    await runRenesasWrite(manager, {
      baud: Number(baud), start: region.start, bytes: writeBytes, erase, verify, idhex: idClean, chip, blocks: region.blocks || [], skipFF,
    }, {
      onLog: (lvl, msg) => pushLog(lvl, msg),
      onProgress: (ph, p) => { setPhase(ph); setProgress(p); },
      onPhase: () => {},
    });
    setBusy(false); setOp(null); setPhase(null); setProgress(0);
  }, [manager, baud, port, region, srcBytes, erase, verify, idArr, chip, absoluteImage, skipFF, pushLog]);

  const download = useCallback(() => {
    if (!result) return;
    let out = result;
    if (absoluteImage && region.start > 0) {
      out = new Uint8Array(region.start + result.length).fill(0xFF);
      out.set(result, region.start);
    }
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const baseStart = (absoluteImage && region.start > 0) ? 0 : region.start;
    const name = `${chip}_${regionKey}_0x${baseStart.toString(16).toUpperCase()}_${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}.bin`;
    const meta = `${out.length} B${absoluteImage && region.start > 0 ? ` (Absolute Image · FF-pad 0x00000000..0x${(region.start - 1).toString(16).toUpperCase().padStart(8, '0')})` : ''}`;
    setSaveAsReq({ bytes: out, name, meta });
  }, [result, chip, regionKey, region, absoluteImage]);

  const inputCls = `w-full ${CMP ? 'h-8 text-[13px]' : 'h-10 text-sm'} rounded-md bg-[#121824] border border-slate-700 text-slate-100 px-3 focus:outline-none focus:ring-2 focus:ring-cyan-500`;

  return (
    <div className="rounded-xl border border-cyan-900/50 bg-[#0a0f16] overflow-hidden h-full flex flex-col" data-testid="renesas-flash-tool">
      <div className={`flex items-center gap-2 px-4 ${CMP ? 'py-1.5' : 'py-3'} border-b border-cyan-900/40 bg-[#0c1420]`}>
        <CircuitBoard className="h-4 w-4 text-cyan-400" />
        <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-bold">Renesas Flash Tool</span>
        <span className="ml-auto flex items-center gap-1.5 text-[11px] font-mono-tt">
          <span className={`inline-block h-2 w-2 rounded-full ${connected ? 'bg-emerald-400' : 'bg-slate-600'}`} data-testid="renesas-conn-dot" />
          {connected ? 'Online' : 'Offline'}
        </span>
      </div>

      <div className={`grid grid-cols-[380px_1fr] max-lg:grid-cols-1 [.ft-narrow_&]:grid-cols-1 flex-1 min-h-0 items-start ${CMP ? 'p-2 gap-2' : 'p-4 gap-4'}`}>
        {/* Controls */}
        <div className={`${CMP ? 'space-y-1.5' : 'space-y-3'}`}>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Chip</label>
            <select data-testid="renesas-chip-select" value={chip} onChange={(e) => onChipChange(e.target.value)} disabled={busy} className={inputCls}>
              {Object.entries(CHIPS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Region</label>
              <select data-testid="renesas-region-select" value={regionKey} onChange={(e) => setRegionKey(e.target.value)} disabled={busy} className={inputCls}>
                {Object.entries(regions).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Baud</label>
              <select data-testid="renesas-baud-select" value={baud} onChange={(e) => setBaud(e.target.value)} disabled={busy} className={inputCls}>
                {BAUDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">ID Code · 7 byte hex (R8C wajib · blank/unlock = FF×7)</label>
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="renesas-id-boxes">
              {idArr.map((v, i) => (
                <input
                  key={i}
                  data-testid={`renesas-id-byte-${i}`}
                  value={v}
                  onChange={(e) => setIdByte(i, e.target.value, e.target)}
                  maxLength={2}
                  placeholder="00"
                  disabled={busy}
                  className={`${CMP ? 'w-9 h-8' : 'w-10 h-10'} rounded-md bg-[#121824] border border-slate-700 text-slate-100 text-center text-sm font-mono-tt uppercase focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-40`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select data-testid="renesas-port-select" value={port} onChange={(e) => setPort(e.target.value)} disabled={busy} className={`${inputCls} flex-1`}>
              <option value="">Auto COM</option>
              {ports.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <button data-testid="renesas-refresh-ports" onClick={loadPorts} disabled={portsLoading || busy}
              className={`${CMP ? 'h-8' : 'h-10'} px-3 rounded-md bg-cyan-500/10 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/20 disabled:opacity-40 inline-flex items-center gap-1.5 text-sm`}>
              <RefreshCw className={`h-4 w-4 ${portsLoading ? 'animate-spin' : ''}`} /> Ports
            </button>
          </div>

          <button data-testid="renesas-read-button" onClick={doRead} disabled={busy}
            className={`w-full ${CMP ? 'h-9' : 'h-11'} rounded-md bg-red-600 hover:bg-red-700 text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40 transition-colors`}>
            <DownloadCloud className="h-4 w-4" /> {op === 'read' ? `Reading… ${progress}%` : 'Read Flash'}
          </button>

          <div className="border-t border-slate-800 my-1" />
          <input ref={fileRef} type="file" accept=".bin,.BIN" className="hidden" data-testid="renesas-file-input"
            onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
          <button data-testid="renesas-open-file" onClick={() => fileRef.current?.click()} disabled={busy}
            className={`w-full ${CMP ? 'h-8' : 'h-10'} rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 disabled:opacity-40 inline-flex items-center justify-center gap-2 text-sm font-semibold`}>
            <FolderOpen className="h-4 w-4" /> {srcName ? `${srcName} (${srcBytes?.length || 0} B)` : 'Open .bin (for Write)'}
          </button>
          <div className="flex items-center gap-4 text-[11px] text-slate-400 px-0.5">
            <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="checkbox" data-testid="renesas-erase-toggle" checked={erase} onChange={(e) => setErase(e.target.checked)} disabled={busy} className="accent-cyan-500" /> Erase</label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="checkbox" data-testid="renesas-verify-toggle" checked={verify} onChange={(e) => setVerify(e.target.checked)} disabled={busy} className="accent-cyan-500" /> Verify</label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer" title="Centang: lewati page kosong (0xFF) — lebih cepat, seperti FDT. Hilangkan centang: tulis SEMUA page (tanpa skip FF).">
              <input type="checkbox" data-testid="renesas-skipff-toggle" checked={skipFF} onChange={(e) => setSkipFF(e.target.checked)} disabled={busy} className="accent-cyan-500" /> Skip FF
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" data-testid="renesas-absolute-toggle" checked={absoluteImage} onChange={(e) => setAbsoluteImage(e.target.checked)} disabled={busy} className="accent-cyan-500" /> Absolute Image
            </label>
          </div>
          <button data-testid="renesas-write-button" onClick={doWrite} disabled={busy || !srcBytes}
            className={`w-full ${CMP ? 'h-9' : 'h-11'} rounded-md border border-[#FF3B30]/50 bg-[#FF3B30]/10 text-[#FF6b63] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#FF3B30]/20 disabled:opacity-40 transition`}>
            <HardDriveDownload className="h-4 w-4" /> {op === 'write' ? `${phase === 'verify' ? 'Verifying' : 'Writing'}… ${progress}%` : 'Write Flash'}
          </button>

          <div className="flex items-center gap-2">
            <button data-testid="renesas-download-button" onClick={download} disabled={!result || busy}
              className={`flex-1 ${CMP ? 'h-8' : 'h-10'} rounded-md border border-emerald-900/60 text-emerald-400 hover:bg-emerald-950/40 disabled:opacity-40 inline-flex items-center justify-center gap-2 text-sm font-semibold`}>
              <Save className="h-4 w-4" /> Save .bin{result ? ` (${(absoluteImage && region.start > 0) ? region.start + result.length : result.length} B)` : ''}
            </button>
            {connected && (
              <button data-testid="renesas-disconnect-button" onClick={doDisconnect} disabled={busy}
                className={`${CMP ? 'h-8' : 'h-10'} px-3 rounded-md border border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-40 inline-flex items-center gap-1.5 text-sm`}>
                <Power className="h-4 w-4" /> Disconnect
              </button>
            )}
          </div>

          {busy && (
            <div className="w-full" data-testid="renesas-progress-text">
              <p className="text-[10px] text-slate-500">{op === 'write' ? (phase === 'verify' ? 'Verifying' : 'Writing') : 'Reading'}… {progress}%</p>
            </div>
          )}
        </div>

        {/* Console */}
        <div className="rounded-lg border border-slate-800 bg-[#080b11] overflow-hidden flex flex-col min-h-0 h-full">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-800 bg-[#0c1017]">
            <Cpu className="h-4 w-4 text-cyan-500" />
            <span className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Renesas Console</span>
            {!autoScroll && (
              <button
                data-testid="renesas-console-follow-btn"
                onClick={() => { setAutoScroll(true); if (consoleRef.current) consoleRef.current.scrollTop = consoleRef.current.scrollHeight; }}
                className="ml-auto text-[10px] px-2 py-0.5 rounded border border-cyan-800 text-cyan-400 hover:bg-cyan-900/30 transition-colors"
              >
                Follow ↓
              </button>
            )}
            <button
              data-testid="renesas-console-copy-btn"
              onClick={copyLogs}
              disabled={!logs.length}
              className={`${autoScroll ? 'ml-auto' : ''} inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${copied ? 'border-emerald-700 text-emerald-400' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="relative h-5 w-full tt-bar-track shrink-0 overflow-hidden" data-testid="renesas-progress">
            <div className={`h-full tt-bar-fill transition-all`} style={{ width: `${progress}%` }} />
            {op && progress > 0 && <span data-testid="renesas-progress-pct" className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-white drop-shadow">{progress}%</span>}
          </div>
          <div ref={consoleRef} onScroll={onConsoleScroll} data-testid="renesas-console" className="p-4 font-mono-tt text-xs space-y-1 overflow-y-auto flex-1 min-h-[300px] lg:min-h-0">
            {logs.length === 0 && <div className="text-slate-600">Select chip &amp; region, then Read Flash…</div>}
            {logs.map((l, i) => (
              <div key={i} className={LEVELS[l.level] || 'text-slate-400'}>
                <span className="text-slate-600 mr-2">{String(i + 1).padStart(2, '0')}</span>{l.msg}
              </div>
            ))}
          </div>
        </div>
      </div>
      {saveAsReq && (
        <SaveAsDialog open title="Save Read Result" defaultName={saveAsReq.name}
          onCancel={() => setSaveAsReq(null)}
          onSave={async (finalName) => { const req = saveAsReq; setSaveAsReq(null); const ok = await saveBinaryWithPicker(req.bytes, finalName, { onLog: pushLog }); if (ok) pushLog('SUCCESS', `Saved: ${finalName} — ${req.meta}`); }} />
      )}
    </div>
  );
};
